import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, RefreshCw, CheckCircle2, AlertTriangle, XCircle, Clock } from "lucide-react";

interface LogRow {
  id: string;
  created_at: string;
  task_key: string;
  function_name: string;
  provider: string | null;
  model_requested: string | null;
  model_used: string | null;
  status: string;
  tokens_prompt: number | null;
  tokens_completion: number | null;
  tokens_total: number | null;
  latency_ms: number;
  fallback_count: number;
  estimated_cost_usd: number | null;
  error_message: string | null;
}

const cardStyle = { background: "hsl(0 0% 8%)", borderColor: "hsl(0 0% 16%)" } as const;

const statusBadge = (s: string) => {
  const map: Record<string, { bg: string; fg: string; icon: JSX.Element }> = {
    success: { bg: "hsl(140 40% 14%)", fg: "hsl(140 70% 70%)", icon: <CheckCircle2 size={11} /> },
    fallback_success: { bg: "hsl(40 50% 16%)", fg: "hsl(40 90% 70%)", icon: <AlertTriangle size={11} /> },
    error: { bg: "hsl(0 50% 18%)", fg: "hsl(0 80% 75%)", icon: <XCircle size={11} /> },
    rate_limited: { bg: "hsl(30 60% 18%)", fg: "hsl(30 90% 70%)", icon: <Clock size={11} /> },
    payment_required: { bg: "hsl(330 50% 18%)", fg: "hsl(330 80% 75%)", icon: <XCircle size={11} /> },
    disabled: { bg: "hsl(0 0% 14%)", fg: "hsl(0 0% 60%)", icon: <XCircle size={11} /> },
  };
  const x = map[s] ?? map.error;
  return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wide" style={{ background: x.bg, color: x.fg }}>
      {x.icon} {s.replace("_", " ")}
    </span>
  );
};

const UsageLogsTable = () => {
  const [rows, setRows] = useState<LogRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [taskFilter, setTaskFilter] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<string>("");

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("ai_usage_logs")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(200);
    if (!error) setRows((data ?? []) as LogRow[]);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const taskKeys = useMemo(() => Array.from(new Set(rows.map((r) => r.task_key))).sort(), [rows]);
  const filtered = useMemo(() => rows.filter((r) =>
    (!taskFilter || r.task_key === taskFilter) && (!statusFilter || r.status === statusFilter)
  ), [rows, taskFilter, statusFilter]);

  const stats = useMemo(() => {
    const total = filtered.length;
    const success = filtered.filter((r) => r.status === "success" || r.status === "fallback_success").length;
    const errors = filtered.filter((r) => r.status === "error" || r.status === "rate_limited" || r.status === "payment_required").length;
    const fallbacks = filtered.filter((r) => r.fallback_count > 0).length;
    const tokens = filtered.reduce((acc, r) => acc + (r.tokens_total ?? 0), 0);
    const avgLatency = total > 0 ? Math.round(filtered.reduce((acc, r) => acc + r.latency_ms, 0) / total) : 0;
    return { total, success, errors, fallbacks, tokens, avgLatency };
  }, [filtered]);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { label: "Calls (last 200)", value: stats.total.toLocaleString() },
          { label: "Successful", value: stats.success.toLocaleString() },
          { label: "Fallbacks used", value: stats.fallbacks.toLocaleString() },
          { label: "Errors", value: stats.errors.toLocaleString() },
          { label: "Avg latency", value: `${stats.avgLatency} ms` },
        ].map((s) => (
          <div key={s.label} className="rounded-lg border p-3" style={cardStyle}>
            <p className="text-[10px] uppercase tracking-wide" style={{ color: "hsl(0 0% 50%)" }}>{s.label}</p>
            <p className="text-lg font-semibold mt-0.5" style={{ color: "hsl(0 0% 95%)" }}>{s.value}</p>
          </div>
        ))}
      </div>
      <div className="text-[11px]" style={{ color: "hsl(0 0% 50%)" }}>
        Total tokens (filtered window): <span style={{ color: "hsl(0 0% 80%)" }}>{stats.tokens.toLocaleString()}</span>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <select
          className="rounded border px-2 py-1.5 text-xs"
          style={{ background: "hsl(0 0% 6%)", borderColor: "hsl(0 0% 20%)", color: "hsl(0 0% 90%)" }}
          value={taskFilter}
          onChange={(e) => setTaskFilter(e.target.value)}
        >
          <option value="">All tasks</option>
          {taskKeys.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <select
          className="rounded border px-2 py-1.5 text-xs"
          style={{ background: "hsl(0 0% 6%)", borderColor: "hsl(0 0% 20%)", color: "hsl(0 0% 90%)" }}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">All statuses</option>
          <option value="success">success</option>
          <option value="fallback_success">fallback_success</option>
          <option value="error">error</option>
          <option value="rate_limited">rate_limited</option>
          <option value="payment_required">payment_required</option>
          <option value="disabled">disabled</option>
        </select>
        <button
          onClick={load}
          className="inline-flex items-center gap-1 px-2 py-1.5 rounded border text-xs"
          style={{ borderColor: "hsl(0 0% 22%)", color: "hsl(0 0% 75%)" }}
        >
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      <div className="rounded-xl border overflow-hidden" style={cardStyle}>
        {loading ? (
          <div className="flex items-center gap-2 p-6 text-sm" style={{ color: "hsl(0 0% 55%)" }}>
            <Loader2 size={14} className="animate-spin" /> Loading usage logs…
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-6 text-sm text-center" style={{ color: "hsl(0 0% 50%)" }}>
            No AI calls match the current filters yet.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead style={{ background: "hsl(0 0% 6%)" }}>
                <tr style={{ color: "hsl(0 0% 55%)" }}>
                  <th className="text-left px-3 py-2 font-medium">When</th>
                  <th className="text-left px-3 py-2 font-medium">Task / Function</th>
                  <th className="text-left px-3 py-2 font-medium">Provider</th>
                  <th className="text-left px-3 py-2 font-medium">Model</th>
                  <th className="text-left px-3 py-2 font-medium">Status</th>
                  <th className="text-right px-3 py-2 font-medium">Tokens</th>
                  <th className="text-right px-3 py-2 font-medium">Cost</th>
                  <th className="text-right px-3 py-2 font-medium">Latency</th>
                  <th className="text-right px-3 py-2 font-medium">FB</th>
                  <th className="text-left px-3 py-2 font-medium">Error</th>
                </tr>
              </thead>
              <tbody style={{ color: "hsl(0 0% 80%)" }}>
                {filtered.map((r) => (
                  <tr key={r.id} className="border-t" style={{ borderColor: "hsl(0 0% 14%)" }}>
                    <td className="px-3 py-2 whitespace-nowrap" style={{ color: "hsl(0 0% 60%)" }}>
                      {new Date(r.created_at).toLocaleString()}
                    </td>
                    <td className="px-3 py-2">
                      <div className="font-mono" style={{ color: "hsl(207 68% 65%)" }}>{r.task_key}</div>
                      <div className="text-[10px]" style={{ color: "hsl(0 0% 50%)" }}>{r.function_name}</div>
                    </td>
                    <td className="px-3 py-2 font-mono text-[11px]" style={{ color: "hsl(42 55% 65%)" }}>
                      {r.provider ?? ", "}
                    </td>
                    <td className="px-3 py-2 font-mono">
                      <div>{r.model_used ?? ", "}</div>
                      {r.model_requested && r.model_requested !== r.model_used && (
                        <div className="text-[10px]" style={{ color: "hsl(0 0% 50%)" }}>requested: {r.model_requested}</div>
                      )}
                    </td>
                    <td className="px-3 py-2">{statusBadge(r.status)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{r.tokens_total?.toLocaleString() ?? ", "}</td>
                    <td className="px-3 py-2 text-right tabular-nums" style={{ color: "hsl(140 50% 70%)" }}>
                      {r.estimated_cost_usd != null ? `$${r.estimated_cost_usd.toFixed(4)}` : ", "}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{r.latency_ms.toLocaleString()} ms</td>
                    <td className="px-3 py-2 text-right tabular-nums">{r.fallback_count > 0 ? r.fallback_count : ", "}</td>
                    <td className="px-3 py-2 max-w-[260px] truncate" title={r.error_message ?? ""} style={{ color: r.error_message ? "hsl(0 80% 75%)" : "hsl(0 0% 50%)" }}>
                      {r.error_message ?? ", "}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default UsageLogsTable;
